import React, { useEffect, useState } from 'react';
import { 
    CheckboxVisibility,
    DetailsList,
    DetailsListLayoutMode,
    IColumn, 
    Image,
    Stack,
    StackItem,
    Text,
    TextField,
} from '@fluentui/react';
import LoaderComponent from '../../components/loader/loader.component';
import { MapContainer, TileLayer } from 'react-leaflet';
import { LatLng } from 'leaflet';
import { AzureDataPosition, AzureMap, AzureMapDataSourceProvider, AzureMapsProvider } from 'react-azure-maps';
import { AuthenticationType } from 'azure-maps-control';
import GoogleMapsMapComponent from '../../components/google-maps/google-maps-map.component';
import GoogleMapMarkerTrackingComponent from '../../components/map-marker/google-map-marker-tracking.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import queryString from "query-string";
import { Buffer } from "buffer";
import LeafletMapMarkerTrackingComponent from '../../components/map-marker/leaflet-map-marker-tracking.component';
import AzureMapMarkerTrackingComponent from '../../components/map-marker/azure-map-marker-tracking.component';
import CheckboxComponent from '../../components/checkbox/checkbox.component';
import moment from 'moment';

import ApiService from '../../services/api/api.service';

import ItemTracking from '../../models/ItemTracking';
import ItemUsuario from '../../models/ItemUsuario';

import connectedIcon from '../../assets/img24/conectado_24.png';

import 'leaflet/dist/leaflet.css';

const getLeafletLatLng = (value: string) : LatLng => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
    }
    else {
        const valueArray : string[] = value.split(';');
        return new LatLng(parseFloat(valueArray[0]), parseFloat(valueArray[1]));
    }
};

const getAzureLatLng = (value: string) : AzureDataPosition => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
    }
    else {
        const valueArray : string[] = value.split(';');
        return [parseFloat(valueArray[1]), parseFloat(valueArray[0])];
    }
};

const getGoogleLatLng = (value: string) : google.maps.LatLngLiteral => {
    if (value.includes(',')) {
        const valueArray : string[] = value.split(',');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
    else {
        const valueArray : string[] = value.split(';');
        return {
            lat: parseFloat(valueArray[0]),
            lng: parseFloat(valueArray[1]),
        };
    }
};

const columns: IColumn[] = [
    { 
        key: '1', 
        name: 'Nombre', 
        minWidth: 100, 
        maxWidth: 200, 
        isResizable: true,
    },
    { 
        key: '2', 
        name: 'FechaPosicion',
        minWidth: 100, 
        maxWidth: 200, 
        isResizable: true,
    },
    { 
        key: '3',
        name: 'EstaConectado', 
        minWidth: 100, 
        maxWidth: 200, 
        isResizable: true,
    },
];

interface IChronologyActualSituationReportPageProps {};

interface IChronologyActualSituationReportPageParams {
    IdUsuario: string;
    CodigoCliente: string;
    TokenSesion: string;
};

const ChronologyActualSituationReportPage = (props: IChronologyActualSituationReportPageProps) => {
    const mapType : string = "leaflet";

    const [loading, setLoading] = useState<boolean>(true);
    const [params, setParams] = useState<IChronologyActualSituationReportPageParams>();
    const [user, setUser] = useState<ItemUsuario>();
    const [trackings, setTrackings] = useState<ItemTracking[]>([]);
    const [selectedTrackings, setSelectedTrackings] = useState<ItemTracking[]>([]);
    const [error, setError] = useState<string>("");
    const [filter, setFilter] = useState<string>("");
    const [checkAll, setCheckAll] = useState<boolean>(true);
    const [updateTime, setUpdateTime] = useState<number>();

    useEffect(() => {
        let queryParams : queryString.ParsedQuery<string> = queryString.parse(window.location.search);
        setParams(JSON.parse(Buffer.from(queryParams.ItemCronologia?.toString() || "", 'base64').toString('ascii')));
    }, []);

    useEffect(() => {
        if (params) {
            let user : ItemUsuario = new ItemUsuario();
            user.IdUsuario = params.IdUsuario;
            user.CodigoCliente = params.CodigoCliente;
            user.Token = params.TokenSesion;
            setUser(user);
        }
    }, [params]);

    useEffect(() => {
        if (user) {
            setLoading(true);
            ApiService.Post<number>("OrdenesTrabajo", "GetChronologyUpdateTime", user, {}, true).then((response: number) => {
                setUpdateTime(response);
            }, () => {
                setUpdateTime(60);
            });
        }
    }, [user]);

    useEffect(() => {
        let timer : NodeJS.Timeout;
        const getData = () : void => {
            if (user && params && updateTime) {
                setLoading(true);
                ApiService.Post<ItemTracking[]>("OrdenesTrabajo", "GetTracking", user, {}, true).then((response: ItemTracking[]) => {
                    timer = setTimeout(() => {
                        getData();
                    }, updateTime * 1000);
                    setTrackings(response.filter((tracking: ItemTracking) => {
                        if (tracking.PosicionGPS && (tracking.PosicionGPS.includes(',') || tracking.PosicionGPS.includes(';'))) {
                            return true;
                        }
                        else {
                            return false;
                        }
                    }));
                    setLoading(false);
                }, (error: string) => {
                    setError(error);
                    setLoading(false);
                });
            }
            else {
                setLoading(false);
            }
        };
    
        getData();

        return () => {
            setTrackings([]);
            setError('');
            setLoading(false);
            clearTimeout(timer);
        };
    }, [user, params, updateTime]);

    useEffect(() => {
        if (trackings && trackings.length) {
            setSelectedTrackings(trackings);
        }
    }, [trackings]);

    useEffect(() => {
        if (checkAll) {
            setSelectedTrackings(trackings);
        }
        else {
            setSelectedTrackings([]);
        }
    }, [checkAll, trackings]);

    const onChangeFilter = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setFilter(newValue || "");
    };

    const onSelectTracking = (tracking: ItemTracking, value: boolean) : void => {
        if (value) {
            setSelectedTrackings([
                ...selectedTrackings,
                tracking,
            ]);
        }
        else {
            setSelectedTrackings(selectedTrackings.filter((t: ItemTracking) => t.IdUsuario !== tracking.IdUsuario));
        }
    };

    const renderItemColumn = (item?: ItemTracking, index?: number, column?: IColumn) : JSX.Element | undefined => {
        if (item && column) {
            if (column.key === "1") {
                return (
                    <CheckboxComponent
                        label={item.Nombre}
                        checked={selectedTrackings.some((t: ItemTracking) => t.IdUsuario === item.IdUsuario)}
                        onChange={(value: boolean) => onSelectTracking(item, value)} />
                );
            }
            else if (column.key === "2") {
                return (
                    <Text>
                        {
                            item.FechaPosicion ? (
                                `${moment(item.FechaPosicion).format("DD-MM-YYYY HH:mm")}h`
                            ) : (
                                "No disponible"
                            )
                        }
                    </Text>
                );
            }
            else if (column.key === "3") {
                <Text>
                    {
                        item.EstaConectado ? (
                            <Image 
                                src={connectedIcon}
                                alt="Conectado" />
                        ) : (
                            <Image 
                                src={connectedIcon}
                                alt="Conectado" />
                        )
                    }
                </Text>
            }
        }
    };

    return (
        <div className='d-flex flex-column h-100 w-100'>
            {
                (!loading && !error) ? (
                    <div className="row m-0 flex-fill overflow-hidden">
                        <div className="col-12 col-md-6 h-50 h-md-100 px-2 overflow-auto">
                            <div className="row m-0">
                                <div className="col-12 py-3">
                                    <Stack 
                                        horizontal={true}
                                        styles={{
                                            root: {
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            },
                                        }}>
                                        <StackItem styles={{
                                            root: {
                                                flex: 1,
                                                paddingLeft: 12,
                                                paddingRight: 12,
                                            },
                                        }}>
                                            <CheckboxComponent
                                                label='TODOS'
                                                checked={checkAll}
                                                onChange={setCheckAll} />
                                        </StackItem>
                                        <StackItem styles={{
                                            root: {
                                                flex: 1,
                                            },
                                        }}>
                                            <TextField
                                                value={filter}
                                                placeholder="Filtrar..."
                                                onChange={onChangeFilter} />
                                        </StackItem>
                                    </Stack>
                                    <DetailsList
                                        items={trackings.filter((t: ItemTracking) => t.Nombre.toLowerCase().includes(filter.toLowerCase()))}
                                        columns={columns}
                                        onRenderItemColumn={renderItemColumn}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        checkboxVisibility={CheckboxVisibility.hidden}
                                        isHeaderVisible={false}
                                        data-is-scrollable="true" />
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6 h-50 h-md-100 overflow-hidden px-2">
                            {
                                selectedTrackings.length ? (
                                    (mapType === "leaflet") ? (
                                        <MapContainer 
                                            center={getLeafletLatLng(selectedTrackings[0].PosicionGPS)} 
                                            zoom={8}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 400,
                                            }}>
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            {
                                                selectedTrackings.map((t: ItemTracking, index: number) => (
                                                    <LeafletMapMarkerTrackingComponent
                                                        key={index.toString()}
                                                        tracking={t} />
                                                ))
                                            }
                                        </MapContainer>
                                    ) : (mapType === "azure") ? (
                                        <AzureMapsProvider>
                                            <div style={{ 
                                                width: '100%',
                                                height: '100%',
                                                minHeight: 400,
                                            }}>
                                                <AzureMap 
                                                    options={{
                                                        authOptions: {
                                                            authType: AuthenticationType.subscriptionKey,
                                                            subscriptionKey: '3Qqlzj_T3oymm37vAdIMIs2H4jUbPZXxtbkHg03LONw',
                                                        },
                                                        center: getAzureLatLng(selectedTrackings[0].PosicionGPS),
                                                        zoom: 8,
                                                    }}>
                                                    <AzureMapDataSourceProvider 
                                                        id={'AzureMapDataSourceProvider'}
                                                        options={{
                                                            lineMetrics: true,
                                                        }}>
                                                        {
                                                            selectedTrackings.map((t: ItemTracking, index: number) => (
                                                                <AzureMapMarkerTrackingComponent
                                                                    key={index.toString()}
                                                                    tracking={t} />
                                                            ))
                                                        }
                                                    </AzureMapDataSourceProvider>
                                                </AzureMap>
                                            </div>
                                        </AzureMapsProvider>
                                    ) : (mapType === "google") ? (
                                        <GoogleMapsMapComponent
                                            zoom={8}
                                            center={getGoogleLatLng(selectedTrackings[0].PosicionGPS)}>
                                            {
                                                selectedTrackings.map((t: ItemTracking, index: number) => (
                                                    <GoogleMapMarkerTrackingComponent
                                                        key={index.toString()}
                                                        tracking={t} />
                                                ))
                                            }
                                        </GoogleMapsMapComponent>
                                    ) : (
                                        null
                                    )
                                ) : (
                                    null
                                )
                            }
                            {
                                !selectedTrackings.length ? (
                                    <ErrorMessageComponent message='No hay datos para mostrar' />
                                ) : (
                                    null
                                )
                            }
                        </div>
                    </div>
                ) : (
                    null
                )
            }
            {
                !loading ? (
                    <ErrorMessageComponent message={error} />
                ) : (
                    null
                )
            }
            <LoaderComponent loading={loading} />
        </div>
    );
};

export default ChronologyActualSituationReportPage;
